import { useState, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  Button,
  Divider,
  Autocomplete,
  Chip,
} from "@mui/material";
import {
  Save as SaveIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import LaboratoryService from '../../../services/laboratoryService';


const AssessorDetailsForm = () => {
  // State management for form fields
  const [loading, setLoading] = useState(false);
  const [disciplines, setDisciplines] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [stateLoading, setStateLoading] = useState(false);
  const [formData, setFormData] = useState({
    assessorName: "",
    assessorId: localStorage.getItem('username'),
    disciplineExpertise: [],
    currentOrganisation: "",
    isGovernmentOrganisation: "",
    organisationAddress: "",
    city: "",
    state: "",
    workingSince: "",
    isNablAccredited: "",
    accreditedDisciplines: [],
  });

  // Load disciplines based on labTypeId and disciplineType from database
  const loadDisciplines = useCallback(async () => {
    setLoading(true);
    try {
      // Get labTypeId from localStorage (default to 3)
      const labTypeId = '3';
      const orgId = '67592';
      const appId = '305';

      let disciplineType = 'Diagnostic'; // default
      const result = await LaboratoryService.getDisciplines(labTypeId, disciplineType, orgId, appId);
      if (result.success) {
        setDisciplines(result.data);
      } else {
        setDisciplines([]);
      }
    } catch (error) {
      console.error('Error loading disciplines:', error);
      setDisciplines([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStates = useCallback(async () => {
  setStateLoading(true);
  try {
    const result = await LaboratoryService.getStates();
    if (result.success) {
      setStateOptions(result.data);
    }
  } catch (error) {
    console.error('Error loading states:', error);
  } finally {
    setStateLoading(false);
  }
}, []);

  useEffect(() => {
    loadDisciplines();
    loadStates();
  }, [loadDisciplines, loadStates]);

  // Handle input changes
  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  // Handle radio button changes
  const handleRadioChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  // Handle form submission
  const handleSubmit = (event) => {
    event.preventDefault();
    console.log("Form submitted:", formData);
    // Add your form submission logic here
    alert("Assessor details submitted successfully!");
    handleReset();
  };

  // Initial data loading
  useEffect(() => {
    loadDisciplines();
    loadStates();
  }, [loadDisciplines, loadStates]);

  // Handle form reset
  const handleReset = () => {
    setFormData({
      assessorName: "",
      assessorId: "",
      disciplineExpertise: [],
      currentOrganisation: "",
      isGovernmentOrganisation: "",
      organisationAddress: "",
      city: "",
      state: "",
      workingSince: "",
      isNablAccredited: "",
      accreditedDisciplines: [],
    });
  };

  return (
    <Box
      sx={{ flexGrow: 1, backgroundColor: "#f5f5f5", minHeight: "100vh", p: 2 }}
    >
      {/* Header Section */}
      <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center"}}>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: "bold", color: "primary.main" }}
            >
              Assessor Details
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Main Form */}
      <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={4}>
              {/* Assessor Name */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ mb: 1, fontWeight: "bold", color: "text.primary" }}
                  >
                    1. Assessor Name *
                  </FormLabel>
                  <TextField
                    fullWidth
                    placeholder="Enter your full name"
                    value={formData.assessorName}
                    onChange={handleInputChange("assessorName")}
                    size="small"
                    required
                  />
                </FormControl>
              </Grid>

              {/* Assessor ID */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ mb: 1, fontWeight: "bold", color: "text.primary" }}
                  >
                    2. Assessor ID *
                  </FormLabel>
                  <TextField
                    fullWidth
                    placeholder="Enter your assessor ID"
                    value={formData.assessorId}
                    onChange={handleInputChange("assessorId")}
                    size="small"
                    inputProps={{ readOnly: true }}
                  />
                </FormControl>
              </Grid>

              {/* Discipline/Expertise */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ mb: 1, fontWeight: "bold", color: "text.primary" }}
                  >
                    3. Discipline / Expertise as per NABL 112 *
                  </FormLabel>
                  <Autocomplete
                    multiple
                    options={disciplines.map((disc) => disc.DisciplineName || disc.disciplineName || disc.name)}
                    value={formData.disciplineExpertise}
                    onChange={(event, newValue) => {
                      setFormData({
                        ...formData,
                        disciplineExpertise: newValue
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select disciplines"
                        size="small"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          {...getTagProps({ index })}
                          sx={{ maxWidth: '200px' }}
                        />
                      ))
                    }
                  />
                </FormControl>
              </Grid>

              {/* Current Organisation */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ mb: 1, fontWeight: "bold", color: "text.primary" }}
                  >
                    4. Current Organisation where working *
                  </FormLabel>
                  <TextField
                    fullWidth
                    placeholder="Enter your current organization name"
                    value={formData.currentOrganisation}
                    onChange={handleInputChange("currentOrganisation")}
                    size="small"
                    required
                  />
                </FormControl>
              </Grid>

              {/* Government Organisation */}
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel
                    component="legend"
                    sx={{ mb: 1, fontWeight: "bold", color: "text.primary" }}
                  >
                    5. Is your current organisation a Government organisation? *
                  </FormLabel>
                  <RadioGroup
                    row
                    value={formData.isGovernmentOrganisation}
                    onChange={handleRadioChange("isGovernmentOrganisation")}
                  >
                    <FormControlLabel
                      value="Yes"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="No"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* Organisation Address */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ mb: 1, fontWeight: "bold", color: "text.primary" }}
                  >
                    6. Current Organisation Address *
                  </FormLabel>
                  <TextField
                    fullWidth
                    placeholder="Enter complete organization address"
                    value={formData.organisationAddress}
                    onChange={handleInputChange("organisationAddress")}
                    size="small"
                    required
                    multiline
                    rows={3}
                  />
                </FormControl>
              </Grid>

              {/* City and State */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ mb: 1, fontWeight: "bold", color: "text.primary" }}
                  >
                    7. City *
                  </FormLabel>
                  <TextField
                    fullWidth
                    placeholder="Enter city"
                    value={formData.city}
                    onChange={handleInputChange("city")}
                    size="small"
                    required
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ mb: 1, fontWeight: "bold", color: "text.primary" }}
                  >
                    8. State *
                  </FormLabel>
                  <Autocomplete
                    options={stateOptions}
                    getOptionLabel={(option) => option.name || option.Name || ""}
                    value={stateOptions.find(state => state.id === formData.state) || null}
                    onChange={(event, newValue) => {
                      setFormData({
                        ...formData,
                        state: newValue ? newValue.id : ""
                      });
                    }}
                    loading={stateLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select state"
                        size="small"
                        required
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              {/* Working Since */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ mb: 1, fontWeight: "bold", color: "text.primary" }}
                  >
                    9. Working since (in current organisation) *
                  </FormLabel>
                  <TextField
                    fullWidth
                    type="date"
                    value={formData.workingSince}
                    onChange={handleInputChange("workingSince")}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </FormControl>
              </Grid>

              {/* NABL Accreditation */}
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel
                    component="legend"
                    sx={{ mb: 1, fontWeight: "bold", color: "text.primary" }}
                  >
                    10. Is your current working organisation NABL accredited? *
                  </FormLabel>
                  <RadioGroup
                    row
                    value={formData.isNablAccredited}
                    onChange={handleRadioChange("isNablAccredited")}
                  >
                    <FormControlLabel
                      value="Yes"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="No"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* Accredited Disciplines - Conditional */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ mb: 1, fontWeight: "bold", color: "text.primary" }}
                  >
                    11. If current working organisation is accredited, mention
                    the disciplines? *
                  </FormLabel>
                  <Autocomplete
                    multiple
                    options={disciplines.map((disc) => disc.DisciplineName || disc.disciplineName || disc.name)}
                    value={formData.accreditedDisciplines}
                    onChange={(event, newValue) => {
                      setFormData({
                        ...formData,
                        accreditedDisciplines: newValue
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={"Select accredited disciplines" }
                        size="small"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          {...getTagProps({ index })}
                          sx={{ maxWidth: '200px' }}
                        />
                      ))
                    }
                  />
                </FormControl>
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                    mt: 3,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    startIcon={<ClearIcon />}
                    sx={{ minWidth: "120px" }}
                  >
                    Reset
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    sx={{ minWidth: "120px" }}
                  >
                    Submit
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AssessorDetailsForm;
